import { useQueryClient } from '@tanstack/react-query';

import useInfiniteQuery from '@/core/hooks/api/useInfiniteQuery';
import Api from '@/core/lib/new-architecture/api';
import getToken from '@/core/lib/new-architecture/query/token';
import type { InfiniteQuery } from '@/core/lib/new-architecture/query/types.query';

const keys = {
  base: ['KEY_TO_PREVENT_COLLISIONS', '/user', '/contacts', '/donations'] as const,
};

type AwaitedContactDonations = Awaited<ReturnType<typeof Api.query.contactDonations.get>>;

const CONTACT_DONATIONS_SIZE = 20;

const useContactsDonations: InfiniteQuery<AwaitedContactDonations, void> = ({ token, enabled }) => {
  return useInfiniteQuery<AwaitedContactDonations>({
    queryKey: keys.base,
    queryFn: async ({ pageParam }) => Api.query.contactDonations.get({ from: pageParam as number }, await getToken(token)),
    enabled,
    initialData: undefined,
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const { length } = pages.flatMap(page => page.donations);
      if (length >= lastPage.total) return undefined;

      return (length / CONTACT_DONATIONS_SIZE) * CONTACT_DONATIONS_SIZE;
    },
  });
};

const useInvalidate = () => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.base });
  return { invalidate };
};

export default { useContactsDonations, useInvalidate };
